// DashboardLayoutEditor.jsx

import React, { useCallback, useMemo } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Services
import {
  buildDashboardEditorDocument,
  collectViewIdsFromBlocks,
  resolveDashboardLayoutDocument,
} from 'services/dashboards/DashboardLayoutModel';
import { updateDashboardLayout } from 'services/dashboards/DashboardWriteService';

// Hooks
import useDashboardLayoutPreviewData from './hooks/useDashboardLayoutPreviewData';

// CSS
import './DashboardLayoutEditor.css';

// Components
import LayoutEditor from 'page/LayoutEditor';

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * DashboardLayoutEditor
 *
 * Renders the shared layout editor configured for dashboard composition.
 *
 * @returns {ReactNode} The rendered dashboard layout editor.
 */
const DashboardLayoutEditor = ({
  allowedTypeKeys = null,
  childNodesByParent = {},
  dashboard = null,
  showLayoutInspector = false,
  userKey = null,
  views = [],
  baseNodes = [],
}) => {
  // ----- CONTEXT ----- //

  const { getAll, getType } = useNodeTypes();

  // ----- MEMOS ----- //

  const resolvedLayoutDocument = useMemo(() => {
    return resolveDashboardLayoutDocument(dashboard);
  }, [dashboard]);

  const editorDocument = useMemo(() => {
    return buildDashboardEditorDocument({
      ...dashboard,
      blocksByKey: resolvedLayoutDocument.blocksByKey,
      layout: resolvedLayoutDocument.layout,
      layoutVersion: resolvedLayoutDocument.layoutVersion,
    });
  }, [dashboard, resolvedLayoutDocument]);

  const existingViewIds = useMemo(() => {
    return collectViewIdsFromBlocks(resolvedLayoutDocument.blocksByKey);
  }, [resolvedLayoutDocument.blocksByKey]);

  const viewBlockAllowedTypeKeys = useMemo(() => {
    if (Array.isArray(allowedTypeKeys) && allowedTypeKeys.length > 0) {
      return allowedTypeKeys;
    }

    return null;
  }, [allowedTypeKeys]);

  const pageViewGetAllTypeDefs = useCallback(() => {
    const allTypeDefs = getAll();
    if (!Array.isArray(viewBlockAllowedTypeKeys) || !viewBlockAllowedTypeKeys.length) {
      return allTypeDefs;
    }

    const allowedSet = new Set(viewBlockAllowedTypeKeys.map((typeKey) => normalizeString(typeKey)).filter(Boolean));
    return allTypeDefs.filter((typeDef) => allowedSet.has(normalizeString(typeDef?.key)));
  }, [getAll, viewBlockAllowedTypeKeys]);

  // ----- HOOKS ----- //

  const previewData = useDashboardLayoutPreviewData({
    blocksByKey: resolvedLayoutDocument.blocksByKey,
    childNodesByParent,
    getType,
    userKey,
    views,
    baseNodes,
  });

  // ----- HANDLERS ----- //

  const handlePersistDocument = useCallback(async (nextLayout, nextBlocksByKey) => {
    const dashboardKey = normalizeString(dashboard?.key);
    if (!dashboardKey) return;

    await updateDashboardLayout(dashboardKey, {
      blocksByKey: nextBlocksByKey,
      layout: nextLayout,
    });
  }, [dashboard?.key]);

  if (!editorDocument) {
    return (
      <div className="dashboard-layout-editor dashboard-layout-editor-empty">
        Dashboard layout is unavailable.
      </div>
    );
  }

  // ----- RENDER ----- //

  return (
    <div className="dashboard-layout-editor">
      <LayoutEditor
        allowContentZoneBlocks={false}
        allowFieldBlocks={false}
        allowNodeBlocks={false}
        allowVideoBlocks={false}
        dashboardKey={dashboard?.key || null}
        dashboardViewAuthoringEnabled
        dashboardViewModalMode="GLOBAL"
        editorDocument={editorDocument}
        layoutKey={dashboard?.key || ''}
        pageViewAuthoringEnabled={false}
        pageViewGetAllTypeDefs={pageViewGetAllTypeDefs}
        showLayoutInspector={showLayoutInspector}
        typeLabelPlural="views"
        viewBlockAllowedTypeKeys={viewBlockAllowedTypeKeys}
        viewBlockExistingViewIds={existingViewIds}
        viewBlockModalCreatedFrom="DASHBOARD"
        viewBlockModalTitle="Add View to Dashboard"
        viewBlockEnforceExplicitProperties={false}
        viewPreviewByBlockKeyOverride={previewData.viewPreviewByBlockKey}
        onPersistDocument={handlePersistDocument}
      />
    </div>
  );
};

// Dev Tools
DashboardLayoutEditor.displayName = 'DashboardLayoutEditor';

export default DashboardLayoutEditor;
